import {
  Box,
  Button,
  Fade,
  Stack,
  LinearProgress,
  Typography,
} from "@mui/material";
import React from "react";
import { useEffect } from "react";
import { useDialog } from "../../context/DialogContext";
import {
  StyledDataGrid,
  titleButton,
  titleButtonInOpp,
} from "../../common/StyledComponents";
import Toolbar from "../../common/Toolbar";
import EditAgent from "../../modal/EditAgent";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import Workspace from "../Workspace";
import ChevronRight from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { rule5properties } from "../../properties";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreateAgent from "../../modal/CreateAgent";
import { peopleAgentType } from "./common/AgentTypes";
import PeopleAgentResults from "./PeopleAgentResults";
import {
  PEOPLE_AGENT_COLUMNS,
  PEOPLE_AGENT_RESULT_COLUMNS,
} from "./common/monitorColumns";
import { NoPeopleAgentsOverlay } from "./common/NoResultsOverlay";
import GenericConfirmation from "../../modal/GenericConfirmation";
import { isSuccessStatus } from "../../common/RequestUtils";
import AgentsToolbar from "./common/AgentsToolbar";

export default function MonitorPeople(props) {
  const dialog = useDialog();
  const history = useHistory();
  const location = useLocation();

  const defaultSearchValue = React.useRef("");

  const [monitorButtonHover, setMonitorButtonHover] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [displayedAgentIndex, setDisplayedAgentIndex] = React.useState(-1);

  function deleteAgent(agent) {
    dialog.openModal(
      "Delete agent",
      GenericConfirmation,
      {
        children: (
          <Typography variant="body1">
            The agent, <strong>{agent.name}</strong>, will be deleted.
          </Typography>
        ),
        confirmFunction: (callback) => {
          axios
            .delete(`${rule5properties.agents}/${agent.agentId}`)
            .then((resp) => {
              if (isSuccessStatus(resp.status)) {
                loadPeopleAgentRows(defaultSearchValue.current);
                if (callback) callback(resp);
              }
            });
        },
      },
      "sm"
    );
  }

  // Callback to modify agent when edited.
  function updateAgentRow(id, newAgentVals) {
    const newRows = [...rows];
    console.log(newRows);
    const editedAgentIndex = rows.findIndex((agent) => agent.agentId === id);
    if (editedAgentIndex >= 0) {
      const tempAgent = {
        ...rows[editedAgentIndex],
        ...newAgentVals,
      };
      newRows.splice(editedAgentIndex, 1, tempAgent);
      setRows(newRows);
    } else {
      console.log("Couldn't find edited agent.");
    }
  }

  const loadPeopleAgentRows = React.useCallback((searchValue) => {
    defaultSearchValue.current = searchValue;
    setLoading(true);
    const params = new URLSearchParams();
    params.append("type", "People");
    if (searchValue) {
      params.append("searchValue", encodeURIComponent(searchValue));
    }
    axios
      .get(rule5properties.agents + "?" + params.toString())
      .then((res) => {
        if (res.status === 200 && res.data) {
          setRows(res.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  }, []);

  // Initial load.
  useEffect(() => {
    loadPeopleAgentRows(defaultSearchValue.current);
    return () => {
      if (processingAgentPollingId.current) {
        console.log("Clearing interval " + processingAgentPollingId.current);
        clearInterval(processingAgentPollingId.current);
      }
    };
  }, []);

  // Keep a running list of processing agents, and routinely check API to see if processing has completed.
  const [processingAgentIds, setProcessingAgentIds] = React.useState([]);
  const processingAgentPollingId = React.useRef(null);
  useEffect(() => {
    const tempProcessingAgentIds = [];
    // Find the agents that are in processing state.
    rows.forEach((agent) => {
      if (
        agent.monitorStatus === "Processing" ||
        agent.monitorStatus === "Failed"
      ) {
        tempProcessingAgentIds.push(agent.agentId);
      }
    });
    setProcessingAgentIds(tempProcessingAgentIds);
  }, [rows]);

  useEffect(() => {
    if (processingAgentIds.length > 0) {
      if (processingAgentPollingId.current === null) {
        processingAgentPollingId.current = setInterval(() => {
          loadPeopleAgentRows(defaultSearchValue.current);
        }, 30000);
      }
    } else {
      clearInterval(processingAgentPollingId.current);
      processingAgentPollingId.current = null;
    }
  }, [processingAgentIds]);

  // Determine if page is currently displaying agent results.
  let showingResults =
    location.pathname.split("/").length > 4 &&
    location.pathname.split("/")[4] === "results";

  return (
    <Box sx={{ height: "100vh" }}>
      <Toolbar>
        <Stack direction="row" alignItems="center">
          <Box
            onClick={() => {
              history.push("/main/monitor/people");
            }}
            sx={showingResults ? titleButtonInOpp : titleButton}
            onMouseEnter={() => {
              setMonitorButtonHover(true);
            }}
            onMouseLeave={() => {
              setMonitorButtonHover(false);
            }}
          >
            People agents
          </Box>
          <Fade in={showingResults && !monitorButtonHover} timeout={300}>
            <Stack direction="row" justifyContent="space-between">
              <ChevronRight
                sx={{
                  transition: ".2s",
                  m: 1,
                  transform: monitorButtonHover ? "scaleX(-1)" : "none",
                }}
              />
            </Stack>
          </Fade>
          <Fade in={showingResults && displayedAgentIndex >= 0} unmountOnExit>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flexGrow: 1 }}
            >
              <span
                style={{
                  transition: "0.3s",
                  opacity: monitorButtonHover ? 0 : 1,
                }}
              >
                {`Results for ${rows[displayedAgentIndex]?.name}`}
              </span>
            </Stack>
          </Fade>
        </Stack>
        {showingResults ? (
          <Button
            startIcon={<ArrowBackIcon />}
            sx={{
              alignSelf: "center",
              mr: 2,
              borderRadius: "8px",
            }}
            disableRipple
            onClick={() => {
              history.push(`/main/monitor/people/`);
            }}
          >
            Return to agents
          </Button>
        ) : (
          <Button
            onClick={() => {
              dialog.openModal(
                "Create people agent",
                CreateAgent,
                {
                  agentType: peopleAgentType,
                  loadAgentRows: loadPeopleAgentRows,
                },
                "lg"
              );
            }}
            variant="contained"
            disableRipple
            disableElevation
            sx={{ alignSelf: "center" }}
          >
            Create people agent
          </Button>
        )}
      </Toolbar>
      <Workspace>
        <Switch>
          <Route
            exact
            path="/main/monitor/people"
            render={() => (
              <StyledDataGrid
                sx={{
                  mx: "30px",
                  "& .MuiDataGrid-cell": {
                    alignSelf: "center",
                  },
                }}
                disableColumnReorder
                rows={rows}
                columns={PEOPLE_AGENT_COLUMNS(
                  loadPeopleAgentRows,
                  deleteAgent,
                  dialog,
                  updateAgentRow
                )}
                loading={loading}
                slots={{
                  toolbar: AgentsToolbar,
                  loadingOverlay: LinearProgress,
                  noRowsOverlay: NoPeopleAgentsOverlay,
                }}
                slotProps={{
                  toolbar: {
                    loadAgentRows: loadPeopleAgentRows,
                    defaultSearchValue: defaultSearchValue.current,
                  },
                }}
                getRowId={(row) => row.agentId}
                getRowHeight={() => "auto"}
                pagination
                disableMultipleRowSelection
                click
                disableVirtualization
                disableColumnMenu
                filterMode="server"
                editMode="row"
                onRowClick={(params, event) => {
                  dialog.openModal(
                    "Configure Agent - " + params.row.name,
                    EditAgent,
                    {
                      defaultPage: "general",
                      modAgent: params.row,
                      loadAgentRows: loadPeopleAgentRows,
                      agentType: peopleAgentType,
                    },
                    "md"
                  );
                }}
              />
            )}
          />
          <Route
            exact
            path="/main/monitor/people/results/:agentId"
            render={() => (
              <PeopleAgentResults
                columns={PEOPLE_AGENT_RESULT_COLUMNS}
                agentRows={rows}
                setDisplayedAgentIndex={setDisplayedAgentIndex}
              />
            )}
          />
        </Switch>
      </Workspace>
    </Box>
  );
}
